// ============================================================================
// CHANGE SOURCE / CHANNEL (surgical attribution correction)
// ============================================================================
// Fixes a wrong OTA / agent / corporate on a booking — commission accounting
// and reporting attribution — without touching money, inventory or status.
// Available on any non-released status including CHECKED_IN / CHECKED_OUT
// (attribution errors surface at reconciliation, after departure).
//
// Deliberate rule: a source correction NEVER re-prices. The struck deal
// stands; if the wrong OTA also means wrong rates, that is a conscious Rate
// Worksheet decision — two facts, two tools, both audited.
//
// Lifecycle-modal pattern: inline state reset on [isOpen, booking], no
// minimize, single submit.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { agentService } from 'api/services/agentService';
import { bookingService } from 'api/services/bookingService';
import { corporateService } from 'api/services/corporateService';
import { otaService } from 'api/services/otaService';
import notificationService from 'services/notificationService';
import type { Booking } from 'api/types/booking.types';
import { BOOKING_SOURCE } from './booking.utils';
import { useBookingSources } from 'hooks/api/useBookingSources';

interface ChangeSourceModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  /** Fired after a successful correction so the list refetches. */
  onChanged: () => void;
}

const ChangeSourceModal: React.FC<ChangeSourceModalProps> = ({
  isOpen, booking, onClose, onChanged,
}) => {
  const { options: sourceOptions, loading: sourcesLoading, error: sourcesError } = useBookingSources();
  const [source, setSource] = useState('');
  const [agentId, setAgentId] = useState('');
  const [corporateId, setCorporateId] = useState('');
  const [otaId, setOtaId] = useState('');
  const [otaRef, setOtaRef] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !booking) return;
    setSource(booking.bookingSource ?? '');
    setAgentId(booking.agentId ?? '');
    setCorporateId(booking.corporateId ?? '');
    setOtaId(booking.otaId ?? '');
    setOtaRef(booking.otaBookingRef ?? '');
    setReason('');
    setError('');
  }, [isOpen, booking]);

  const loadAgents = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await agentService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data || []).map((a: any) => ({ value: a.id, label: a.agentName ?? a.name }));
  }, []);
  const loadCorporates = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await corporateService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data || []).map((c: any) => ({ value: c.id, label: c.corporateName ?? c.name }));
  }, []);
  const loadOtas = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await otaService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data || []).map((o: any) => ({ value: o.id, label: o.displayName ?? o.otaName ?? o.name }));
  }, []);

  const showAgent = source === BOOKING_SOURCE.TRAVEL_AGENT;
  const showCorporate = source === 'Corporate';
  const showOta = source === 'OTA';

  const handleSave = useCallback(async () => {
    if (!booking) return;
    if (!source) { setError('Pick the correct source.'); return; }
    if (showAgent && !agentId) { setError('A Travel Agent booking needs the agent.'); return; }
    if (showCorporate && !corporateId) { setError('A Corporate booking needs the corporate account.'); return; }
    if (showOta && !otaId) { setError('An OTA booking needs the OTA.'); return; }
    if (!reason.trim()) { setError('A reason is required for a source correction.'); return; }
    setError('');
    setSaving(true);
    try {
      await bookingService.changeSource(booking.bookingId, {
        bookingSource: source,
        agentId: showAgent ? agentId : undefined,
        corporateId: showCorporate ? corporateId : undefined,
        otaId: showOta ? otaId : undefined,
        otaBookingRef: showOta ? (otaRef.trim() || undefined) : undefined,
        reason: reason.trim(),
      });
      notificationService.toast.success(`Source corrected for "${booking.bookingNumber}".`);
      onChanged();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Source correction failed.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [booking, source, showAgent, agentId, showCorporate, corporateId, showOta, otaId, otaRef, reason, onChanged, onClose]);

  if (!isOpen || !booking) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Change Source / Channel — {booking.bookingNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
            Current: <b style={{ color: 'var(--text-primary)' }}>{booking.bookingSource}</b>
            {booking.agentName ? ` · ${booking.agentName}` : ''}
            {booking.corporateName ? ` · ${booking.corporateName}` : ''}
            {booking.otaDisplayName ? ` · ${booking.otaDisplayName}` : ''}
            {booking.otaBookingRef ? ` · Ref ${booking.otaBookingRef}` : ''}
            <span style={{ display: 'block' }}>
              Corrects attribution only — rates are never touched here; a money
              consequence is a Rate Worksheet decision.
            </span>
          </div>

          {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}

          <div className="form-group">
            <SyutSelect
              label="Source"
              required
              options={sourceOptions}
              value={source}
              onChange={(v) => setSource((v as string) || '')}
              isClearable={false}
              isDisabled={saving || sourcesLoading}
              placeholder={sourcesLoading ? 'Loading sources...' : undefined}
              error={sourcesError ?? undefined}
              size="sm"
              // The booking's current source may have been retired from the enum
              // since it was taken; seed it so this control never renders blank
              // on an existing record.
              seedOptions={
                source && !sourceOptions.some((o) => o.value === source)
                  ? [{ value: source, label: source }]
                  : undefined
              }
            />
          </div>

          {showAgent && (
            <div className="form-group">
              <SyutSelect
                label="Agent" required isAsync loadOptions={loadAgents}
                defaultOptions cacheOptions={false}
                seedOptions={booking.agentId && booking.agentName ? [{ value: booking.agentId, label: booking.agentName }] : undefined}
                value={agentId} onChange={(v) => setAgentId((v as string) || '')}
                isClearable={false} isDisabled={saving} size="sm"
                menuPortalTarget={document.body}
              />
            </div>
          )}
          {showCorporate && (
            <div className="form-group">
              <SyutSelect
                label="Corporate" required isAsync loadOptions={loadCorporates}
                defaultOptions cacheOptions={false}
                seedOptions={booking.corporateId && booking.corporateName ? [{ value: booking.corporateId, label: booking.corporateName }] : undefined}
                value={corporateId} onChange={(v) => setCorporateId((v as string) || '')}
                isClearable={false} isDisabled={saving} size="sm"
                menuPortalTarget={document.body}
              />
            </div>
          )}
          {showOta && (
            <>
              <div className="form-group">
                <SyutSelect
                  label="OTA" required isAsync loadOptions={loadOtas}
                  defaultOptions cacheOptions={false}
                  seedOptions={booking.otaId && booking.otaDisplayName ? [{ value: booking.otaId, label: booking.otaDisplayName }] : undefined}
                  value={otaId} onChange={(v) => setOtaId((v as string) || '')}
                  isClearable={false} isDisabled={saving} size="sm"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="form-group">
                <label className="form-label">OTA booking reference</label>
                <input
                  type="text" maxLength={100}
                  className="form-input"
                  value={otaRef}
                  onChange={(e) => setOtaRef(e.target.value)}
                  disabled={saving}
                />
              </div>
            </>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <textarea
              className="form-input form-textarea"
              rows={2} maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why the attribution is being corrected — the audit anchor…"
              disabled={saving}
              style={{ fontSize: '0.8rem' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Correct Source'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChangeSourceModal;
